/**
 * Dream Graph Expression Language Parser
 *
 * Implements a Pratt (top-down operator precedence) parser for the Dream Graph
 * scripting language. The parser converts a string of code into an Abstract Syntax
 * Tree (AST) suitable for evaluation.
 *
 * Pratt parsing is chosen over recursive descent because it elegantly handles
 * operator precedence without building a separate precedence table. Each operator
 * is associated with a binding power, and the parser uses that to determine when
 * to stop parsing a subexpression.
 *
 * **Error Handling:** All parser errors are caught and returned in a ParseResult,
 * never thrown. This allows the UI to display parse errors gracefully.
 *
 * @module scripting/parser
 */

import type {
  ASTNode,
  AssignmentNode,
  BinaryOpNode,
  LiteralNode,
  ParseResult,
  SequenceNode,
  UnaryOpNode,
  VariableNode,
} from './types';

/**
 * Token Type Definition
 *
 * Represents a single token from the lexer.
 * Contains the token type, the string value, and position in the source for errors.
 */
interface Token {
  type:
    | 'EOF'
    | 'NUMBER'
    | 'STRING'
    | 'IDENT'
    | 'TRUE'
    | 'FALSE'
    | 'PLUS'
    | 'MINUS'
    | 'STAR'
    | 'SLASH'
    | 'EQ'
    | 'NE'
    | 'LT'
    | 'GT'
    | 'LE'
    | 'GE'
    | 'AND'
    | 'OR'
    | 'NOT'
    | 'ASSIGN'
    | 'PLUS_ASSIGN'
    | 'MINUS_ASSIGN'
    | 'STAR_ASSIGN'
    | 'SLASH_ASSIGN'
    | 'LPAREN'
    | 'RPAREN'
    | 'DOT'
    | 'SEMICOLON'
    | 'WHITESPACE';
  value: string;
  position: number;
}

/**
 * Lexer: Converts source code into tokens
 *
 * This lexer handles single-character tokens, multi-character operators,
 * identifiers, and literals. It skips whitespace (which is consumed but not
 * tokenized, as it's not relevant to parsing).
 *
 * The lexer maintains position information for error reporting.
 */
class Lexer {
  private pos = 0;
  private tokens: Token[] = [];

  constructor(private source: string) {
    this.tokenize();
  }

  /**
   * Main tokenization loop
   *
   * Iterates through the source, creating tokens until EOF is reached.
   */
  private tokenize(): void {
    while (this.pos < this.source.length) {
      const ch = this.source[this.pos];

      if (/\s/.test(ch)) {
        this.pos++;
        continue;
      }

      if (ch === '(') {
        this.addToken('LPAREN', '(');
        this.pos++;
      } else if (ch === ')') {
        this.addToken('RPAREN', ')');
        this.pos++;
      } else if (ch === '.') {
        this.addToken('DOT', '.');
        this.pos++;
      } else if (ch === ';') {
        this.addToken('SEMICOLON', ';');
        this.pos++;
      } else if (ch === '+') {
        if (this.peek() === '=') {
          this.addToken('PLUS_ASSIGN', '+=');
          this.pos += 2;
        } else {
          this.addToken('PLUS', '+');
          this.pos++;
        }
      } else if (ch === '-') {
        if (this.peek() === '=') {
          this.addToken('MINUS_ASSIGN', '-=');
          this.pos += 2;
        } else {
          this.addToken('MINUS', '-');
          this.pos++;
        }
      } else if (ch === '*') {
        if (this.peek() === '=') {
          this.addToken('STAR_ASSIGN', '*=');
          this.pos += 2;
        } else {
          this.addToken('STAR', '*');
          this.pos++;
        }
      } else if (ch === '/') {
        if (this.peek() === '=') {
          this.addToken('SLASH_ASSIGN', '/=');
          this.pos += 2;
        } else {
          this.addToken('SLASH', '/');
          this.pos++;
        }
      } else if (ch === '=') {
        if (this.peek() === '=') {
          this.addToken('EQ', '==');
          this.pos += 2;
        } else {
          this.addToken('ASSIGN', '=');
          this.pos++;
        }
      } else if (ch === '!') {
        if (this.peek() === '=') {
          this.addToken('NE', '!=');
          this.pos += 2;
        } else {
          this.addToken('NOT', '!');
          this.pos++;
        }
      } else if (ch === '<') {
        if (this.peek() === '=') {
          this.addToken('LE', '<=');
          this.pos += 2;
        } else {
          this.addToken('LT', '<');
          this.pos++;
        }
      } else if (ch === '>') {
        if (this.peek() === '=') {
          this.addToken('GE', '>=');
          this.pos += 2;
        } else {
          this.addToken('GT', '>');
          this.pos++;
        }
      } else if (ch === '&') {
        if (this.peek() === '&') {
          this.addToken('AND', '&&');
          this.pos += 2;
        } else {
          throw new Error(`Unexpected character: &`);
        }
      } else if (ch === '|') {
        if (this.peek() === '|') {
          this.addToken('OR', '||');
          this.pos += 2;
        } else {
          throw new Error(`Unexpected character: |`);
        }
      } else if (/[0-9]/.test(ch)) {
        this.lexNumber();
      } else if (ch === '"' || ch === "'") {
        this.lexString(ch);
      } else if (/[a-zA-Z_]/.test(ch)) {
        this.lexIdentifier();
      } else {
        throw new Error(`Unexpected character: ${ch}`);
      }
    }
    this.addToken('EOF', '');
  }

  /**
   * Peek at the next character without consuming it
   */
  private peek(): string {
    return this.pos + 1 < this.source.length ? this.source[this.pos + 1] : '';
  }

  /**
   * Add a token to the token stream
   */
  private addToken(type: Token['type'], value: string): void {
    this.tokens.push({ type, value, position: this.pos });
  }

  /**
   * Lex a number literal (integer or float)
   *
   * Supports: `42`, `3.14`, `.5` (leading decimal)
   */
  private lexNumber(): void {
    const start = this.pos;
    while (this.pos < this.source.length && /[0-9.]/.test(this.source[this.pos])) {
      this.pos++;
    }
    const value = this.source.substring(start, this.pos);
    this.tokens.push({ type: 'NUMBER', value, position: start });
  }

  /**
   * Lex a string literal with quote character handling
   *
   * Supports both single and double quotes. Handles escape sequences (\").
   */
  private lexString(quote: string): void {
    const start = this.pos;
    this.pos++;
    let value = '';
    while (this.pos < this.source.length) {
      const ch = this.source[this.pos];
      if (ch === quote) {
        this.pos++;
        break;
      }
      if (ch === '\\') {
        this.pos++;
        if (this.pos < this.source.length) {
          const escaped = this.source[this.pos];
          value += escaped === 'n' ? '\n' : escaped === 't' ? '\t' : escaped;
          this.pos++;
        }
      } else {
        value += ch;
        this.pos++;
      }
    }
    this.tokens.push({ type: 'STRING', value, position: start });
  }

  /**
   * Lex an identifier or keyword
   *
   * Keywords like 'true', 'false' are handled here.
   * Otherwise, the identifier can be used as a variable name.
   */
  private lexIdentifier(): void {
    const start = this.pos;
    while (this.pos < this.source.length && /[a-zA-Z0-9_]/.test(this.source[this.pos])) {
      this.pos++;
    }
    const value = this.source.substring(start, this.pos);
    let type: Token['type'] = 'IDENT';
    if (value === 'true') type = 'TRUE';
    else if (value === 'false') type = 'FALSE';
    this.tokens.push({ type, value, position: start });
  }

  /**
   * Get all tokens produced by the lexer
   */
  getTokens(): Token[] {
    return this.tokens;
  }
}

/**
 * Parser: Converts tokens into an Abstract Syntax Tree (AST)
 *
 * Implements the Pratt parsing algorithm with operator precedence binding powers.
 * The parser maintains the current token position and provides methods to:
 * - Check the current token
 * - Consume tokens
 * - Report parse errors
 * - Parse expressions with operator precedence
 */
class Parser {
  private tokens: Token[];
  private pos = 0;

  constructor(tokens: Token[]) {
    this.tokens = tokens;
  }

  /**
   * Parse the token stream into an AST
   *
   * The top-level parse method handles statement sequences and EOF.
   * If the source contains multiple statements separated by semicolons,
   * they are combined into a Sequence node.
   */
  parse(): ASTNode {
    const statements: ASTNode[] = [];
    while (!this.isAtEnd()) {
      statements.push(this.parseStatement());
      if (this.match('SEMICOLON')) {
        // Consume semicolon, continue
      } else if (!this.isAtEnd()) {
        throw new Error('Expected ; between statements');
      }
    }
    if (statements.length === 0) {
      throw new Error('Empty program');
    }
    return statements.length === 1 ? statements[0] : { type: 'Sequence', statements };
  }

  /**
   * Parse a single statement
   *
   * Statements can be assignments (variables on the left, expressions on the right)
   * or pure expressions (assignments are a special case of expressions in this grammar).
   */
  private parseStatement(): ASTNode {
    return this.parseAssignment();
  }

  /**
   * Parse assignment expressions
   *
   * Grammar: Variable CompoundOp LogicalOr
   *
   * If the next expression is a variable followed by an assignment operator,
   * parse it as an assignment. Otherwise, just parse a logical OR expression.
   */
  private parseAssignment(): ASTNode {
    const expr = this.parseLogicalOr();

    if (expr.type === 'Variable' && this.isAssignmentOperator()) {
      const target = expr as VariableNode;
      const operator = this.current().value as '=' | '+=' | '-=' | '*=' | '/=';
      this.advance();
      const value = this.parseAssignment();
      return {
        type: 'Assignment',
        target,
        operator,
        value,
      } as AssignmentNode;
    }

    return expr;
  }

  /**
   * Check if current token is an assignment operator
   */
  private isAssignmentOperator(): boolean {
    const type = this.current().type;
    return ['ASSIGN', 'PLUS_ASSIGN', 'MINUS_ASSIGN', 'STAR_ASSIGN', 'SLASH_ASSIGN'].includes(
      type
    );
  }

  /**
   * Parse logical OR expressions (lowest precedence)
   *
   * Grammar: LogicalAnd ('||' LogicalAnd)*
   * Binding power: 1
   */
  private parseLogicalOr(): ASTNode {
    let expr = this.parseLogicalAnd();
    while (this.match('OR')) {
      const operator = '||';
      const right = this.parseLogicalAnd();
      expr = { type: 'BinaryOp', operator, left: expr, right } as BinaryOpNode;
    }
    return expr;
  }

  /**
   * Parse logical AND expressions
   *
   * Grammar: Equality ('&&' Equality)*
   * Binding power: 2
   */
  private parseLogicalAnd(): ASTNode {
    let expr = this.parseEquality();
    while (this.match('AND')) {
      const operator = '&&';
      const right = this.parseEquality();
      expr = { type: 'BinaryOp', operator, left: expr, right } as BinaryOpNode;
    }
    return expr;
  }

  /**
   * Parse equality expressions (==, !=)
   *
   * Grammar: Comparison (('==' | '!=') Comparison)*
   * Binding power: 3
   */
  private parseEquality(): ASTNode {
    let expr = this.parseComparison();
    while (this.isOneOf('EQ', 'NE')) {
      const operator = (this.current().value === '==' ? '==' : '!=') as '==' | '!=';
      this.advance();
      const right = this.parseComparison();
      expr = { type: 'BinaryOp', operator, left: expr, right } as BinaryOpNode;
    }
    return expr;
  }

  /**
   * Parse comparison expressions (<, >, <=, >=)
   *
   * Grammar: Addition (('<' | '>' | '<=' | '>=') Addition)*
   * Binding power: 4
   */
  private parseComparison(): ASTNode {
    let expr = this.parseAddition();
    while (this.isOneOf('LT', 'GT', 'LE', 'GE')) {
      const operator = this.current().value as '<' | '>' | '<=' | '>=';
      this.advance();
      const right = this.parseAddition();
      expr = { type: 'BinaryOp', operator, left: expr, right } as BinaryOpNode;
    }
    return expr;
  }

  /**
   * Parse addition/subtraction expressions
   *
   * Grammar: Multiplication (('+' | '-') Multiplication)*
   * Binding power: 5
   */
  private parseAddition(): ASTNode {
    let expr = this.parseMultiplication();
    while (this.isOneOf('PLUS', 'MINUS')) {
      const operator = this.current().value as '+' | '-';
      this.advance();
      const right = this.parseMultiplication();
      expr = { type: 'BinaryOp', operator, left: expr, right } as BinaryOpNode;
    }
    return expr;
  }

  /**
   * Parse multiplication/division expressions
   *
   * Grammar: Unary (('*' | '/') Unary)*
   * Binding power: 6
   */
  private parseMultiplication(): ASTNode {
    let expr = this.parseUnary();
    while (this.isOneOf('STAR', 'SLASH')) {
      const operator = this.current().value as '*' | '/';
      this.advance();
      const right = this.parseUnary();
      expr = { type: 'BinaryOp', operator, left: expr, right } as BinaryOpNode;
    }
    return expr;
  }

  /**
   * Parse unary expressions (!, logical NOT)
   *
   * Grammar: ('!')* Postfix
   * Binding power: 7 (highest of prefix operators)
   *
   * Supports chaining: `!!x` is parsed as NOT(NOT(x))
   */
  private parseUnary(): ASTNode {
    if (this.match('NOT')) {
      const operand = this.parseUnary();
      return { type: 'UnaryOp', operator: '!', operand } as UnaryOpNode;
    }
    return this.parsePostfix();
  }

  /**
   * Parse postfix expressions (for future extension, currently just calls parsePrimary)
   *
   * Postfix operators like array access or method calls could be added here.
   */
  private parsePostfix(): ASTNode {
    return this.parsePrimary();
  }

  /**
   * Parse primary expressions: literals, variables, and parenthesized expressions
   *
   * Grammar: Variable | Literal | '(' LogicalOr ')'
   */
  private parsePrimary(): ASTNode {
    if (this.match('TRUE')) {
      return { type: 'Literal', value: true, dataType: 'boolean' } as LiteralNode;
    }

    if (this.match('FALSE')) {
      return { type: 'Literal', value: false, dataType: 'boolean' } as LiteralNode;
    }

    if (this.match('NUMBER')) {
      const value = parseFloat(this.previous().value);
      return { type: 'Literal', value, dataType: 'number' } as LiteralNode;
    }

    if (this.match('STRING')) {
      const value = this.previous().value;
      return { type: 'Literal', value, dataType: 'string' } as LiteralNode;
    }

    if (this.match('IDENT')) {
      return this.parseVariable();
    }

    if (this.match('LPAREN')) {
      const expr = this.parseLogicalOr();
      if (!this.match('RPAREN')) {
        throw new Error('Expected )');
      }
      return expr;
    }

    throw new Error(`Unexpected token: ${this.current().value}`);
  }

  /**
   * Parse variable references with dot notation
   *
   * Grammar: Identifier ('.' Identifier)*
   * Examples: `quest`, `quest.stage`, `player.inventory.size`
   *
   * Returns a VariableNode with:
   * - `name`: the full qualified name (e.g., "quest.stage")
   * - `parts`: the split parts (e.g., ["quest", "stage"])
   */
  private parseVariable(): VariableNode {
    const parts = [this.previous().value];
    while (this.match('DOT')) {
      if (!this.match('IDENT')) {
        throw new Error('Expected identifier after .');
      }
      parts.push(this.previous().value);
    }
    const name = parts.join('.');
    return { type: 'Variable', name, parts } as VariableNode;
  }

  /**
   * Advance to the next token (consuming the current one)
   */
  private advance(): Token {
    if (!this.isAtEnd()) this.pos++;
    return this.previous();
  }

  /**
   * Check if current token matches any of the given types
   */
  private isOneOf(...types: Token['type'][]): boolean {
    return types.includes(this.current().type);
  }

  /**
   * If current token is of given type, consume it and return true
   */
  private match(...types: Token['type'][]): boolean {
    if (this.isOneOf(...types)) {
      this.advance();
      return true;
    }
    return false;
  }

  /**
   * Get the current token
   */
  private current(): Token {
    return this.tokens[this.pos];
  }

  /**
   * Get the previous token (the one we just consumed)
   */
  private previous(): Token {
    return this.tokens[this.pos - 1];
  }

  /**
   * Check if we've reached the end of the token stream
   */
  private isAtEnd(): boolean {
    return this.current().type === 'EOF';
  }
}

/**
 * Parse a script into an Abstract Syntax Tree
 *
 * This is the public entry point for the parser. It handles lexing and parsing,
 * catching any errors and returning them in a ParseResult.
 *
 * @param source The source code string to parse
 * @returns A ParseResult with either the AST or an error message
 *
 * @example
 * ```typescript
 * const result = parseScript("quest.stage = 2; gold += 10");
 * if (result.success) {
 *   console.log(result.ast);
 * } else {
 *   console.error(result.error);
 * }
 * ```
 */
export function parseScript(source: string): ParseResult {
  try {
    const lexer = new Lexer(source);
    const tokens = lexer.getTokens();
    const parser = new Parser(tokens);
    const ast = parser.parse();
    return { success: true, ast };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
